import React, { useState, useRef, useEffect } from 'react';
import './ImageDesigner.css';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Crop as CropIcon,
  Undo as UndoIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import ImageCropModal from '../ImageCropModal';
import { createPreviewUrl, getMediaDisplayUrl, revokePreviewUrl } from '../../utils/mediaUtils';

const getImageIdentity = (img) => {
  if (!img) return null;
  if (img.id != null) return `id:${img.id}`;
  if (img.url) return `url:${img.url}`;
  if (img.displayUrl) return `display:${img.displayUrl}`;
  return null;
};

const ImageDesigner = ({ image, onImageChange, showActionsBelow = false, variant = 'default', formatHint }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(image || null);
  const [imageBeforeCrop, setImageBeforeCrop] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [altText, setAltText] = useState('');
  const fileInputRef = useRef(null);
  const imageIdentityRef = useRef(null);
  const preserveCropUndoRef = useRef(false);

  // Initialiser le texte alt quand une image est chargée
  useEffect(() => {
    if (selectedImage) {
      setAltText(selectedImage.alt || '');
    }
  }, [selectedImage]);

  const updateImageWithAlt = (imageObj, newAltText) => {
    const updatedImage = { ...imageObj, alt: newAltText };
    setSelectedImage(updatedImage);
    onImageChange(updatedImage);
  };

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const displayUrl = createPreviewUrl(file);
      const newImage = {
        file,
        displayUrl,
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        alt: '',
      };
      setSelectedImage(newImage);
      setImageBeforeCrop(null);
      onImageChange(newImage);
    }
  };

  const handleDeleteImage = () => {
    revokePreviewUrl(selectedImage);
    setSelectedImage(null);
    setImageBeforeCrop(null); // Nettoyer aussi l'état de recadrage
    setAltText('');
    onImageChange(null);
  };

  const handleRestoreOriginal = () => {
    if (originalImage) {
      setSelectedImage(originalImage);
      setAltText(originalImage.alt || '');
      onImageChange(originalImage);
    }
  };

  const handleOpenCropModal = () => {
    setIsCropModalOpen(true);
  };

  const handleCloseCropModal = () => {
    setIsCropModalOpen(false);
  };

  const handleCropComplete = (croppedImage) => {
    revokePreviewUrl(selectedImage);
    setImageBeforeCrop(selectedImage);
    preserveCropUndoRef.current = true;
    const imageWithAlt = { ...croppedImage, alt: altText };
    setSelectedImage(imageWithAlt);
    onImageChange(imageWithAlt);
    setIsCropModalOpen(false);
  };

  const handleUndoCrop = () => {
    if (imageBeforeCrop) {
      revokePreviewUrl(selectedImage);
      preserveCropUndoRef.current = true;
      setSelectedImage(imageBeforeCrop);
      setAltText(imageBeforeCrop.alt || '');
      onImageChange(imageBeforeCrop);
      setImageBeforeCrop(null);
    }
  };

  const handleAltTextChange = (e) => {
    const newAltText = e.target.value;
    setAltText(newAltText);
    if (selectedImage) {
      updateImageWithAlt(selectedImage, newAltText);
    }
  };

  useEffect(() => {
    const identity = getImageIdentity(image);

    if (identity === imageIdentityRef.current) {
      return;
    }

    imageIdentityRef.current = identity;

    if (!image) {
      setSelectedImage(null);
      setOriginalImage(null);
      setAltText('');
      setImageBeforeCrop(null);
      return;
    }

    const imageWithAlt = { ...image, alt: image.alt || '' };
    setSelectedImage(imageWithAlt);
    setOriginalImage(imageWithAlt);
    setAltText(image.alt || '');

    if (!preserveCropUndoRef.current) {
      setImageBeforeCrop(null);
    }
    preserveCropUndoRef.current = false;
  }, [image]);

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const cropAspectRatio = variant === 'square' ? 1 : variant === 'banner' ? 3 : null;

  return (
    <div className={`image-designer image-designer--${variant} ${showActionsBelow ? 'actions-below' : ''}`}>
      {selectedImage ? (
        <>
          <div className="image-preview-area">
            <div className="image-container">
              <img
                src={getMediaDisplayUrl(selectedImage)}
                alt={selectedImage.alt || selectedImage.name}
                className="image-preview"
              />
            </div>
            {!showActionsBelow && (
              <div className="image-actions">
                <button
                  type="button"
                  className="image-action-button crop"
                  onClick={handleOpenCropModal}
                  title="Recadrer l'image"
                  style={{ padding: '5px 10px' }}
                >
                  <CropIcon fontSize="small" />
                </button>
                {imageBeforeCrop && (
                  <button
                    type="button"
                    className="image-action-button undo"
                    onClick={handleUndoCrop}
                    title="Annuler le dernier recadrage"
                    style={{ padding: '5px 10px' }}
                  >
                    <UndoIcon fontSize="small" />
                  </button>
                )}
                <button
                  type="button"
                  className="image-action-button delete"
                  onClick={handleDeleteImage}
                  title="Supprimer l'image"
                  style={{ padding: '5px 10px' }}
                >
                  <DeleteIcon fontSize="small" />
                </button>
              </div>
            )}
          </div>

          {/* Section du texte alt */}
          <div className="alt-text-section">
            <div className="alt-text-label">
              <EditIcon fontSize="small" className="alt-icon" />
              <span>Texte alternatif</span>
            </div>

            <input
              type="text"
              value={altText}
              onChange={handleAltTextChange}
              placeholder="Décrivez cette image pour l'accessibilité et le référencement..."
              className="alt-text-input"
              maxLength={200}
            />

            <div className="alt-text-helper">
              {altText.length}/200 caractères
              {altText.length === 0 && (
                <span className="alt-text-warning"> • Recommandé pour l'accessibilité et le référencement</span>
              )}
            </div>
          </div>

          {showActionsBelow && (
            <div className="image-actions-below">
              <button
                type="button"
                className="image-action-button-below crop"
                onClick={handleOpenCropModal}
                title="Recadrer l'image"
              >
                <CropIcon fontSize="small" />
                <span>Recadrer</span>
              </button>
              {imageBeforeCrop && (
                <button
                  type="button"
                  className="image-action-button-below undo"
                  onClick={handleUndoCrop}
                  title="Annuler le dernier recadrage"
                >
                  <UndoIcon fontSize="small" />
                  <span>Annuler</span>
                </button>
              )}
              <button
                type="button"
                className="image-action-button-below delete"
                onClick={handleDeleteImage}
                title="Supprimer l'image"
              >
                <DeleteIcon fontSize="small" />
                <span>Supprimer</span>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="image-upload-container" onClick={triggerFileInput}>
          <div className="image-upload-content">
            <UploadIcon className="upload-icon" />
            <h3>Déposez votre image ici</h3>
            <p>ou cliquez pour parcourir vos fichiers</p>
            <p className="file-types">JPG, PNG, GIF (max 5 MB)</p>
            {formatHint && <p className="format-hint">{formatHint}</p>}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            className="image-upload-input"
          />
        </div>
      )}
      {!selectedImage && originalImage && (
        <button
          type="button"
          className="image-action-button restore"
          onClick={handleRestoreOriginal}
          title="Restaurer l'image originale"
          style={{ padding: '5px 10px' }}
        >
          <RestoreIcon fontSize="small" />
        </button>
      )}

      <ImageCropModal
        isOpen={isCropModalOpen}
        image={selectedImage}
        aspectRatio={cropAspectRatio}
        onCropComplete={handleCropComplete}
        onClose={handleCloseCropModal}
      />
    </div>
  );
};

export default ImageDesigner;
